import { OCR_BASE_URL } from '#/config/consts';
import { useState, useCallback } from 'react';

export type OcrFieldResult = {
  [key: string]: string;
};

export type OcrResult = {
  result: OcrFieldResult | null;
  error: string | null;
};

export type OcrFieldResponse = {
  question: string;
  confidence: number;
  answer: string;
};
export interface OcrServiceResponse {
  success: boolean;
  data?: OcrFieldResponse[];
  message?: string;
}

// Convert File to Base64
const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      // Remove data URL prefix (data:image/jpeg;base64,)
      const base64 = reader.result?.toString().split(',')[1];
      resolve(base64 || '');
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

// Validate file before upload
const validateFile = (file: File): boolean => {
  const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/tiff'];
  const maxSize = 5 * 1024 * 1024; // 5MB

  if (!allowedTypes.includes(file.type)) {
    throw new Error('File type not supported');
  }

  if (file.size > maxSize) {
    throw new Error('File size exceeds 5MB limit');
  }

  return true;
};

// Error handling
const handleOCRResponse = (response: Response, data: OcrFieldResponse[]): OcrServiceResponse => {
  const errorMap: Record<number, string> = {
    200: 'OCR processing successful',
    204: 'No readable content found in image',
    400: 'Invalid request format',
    413: 'Please use a smaller image (max 5MB)',
    415: 'Please use JPEG, PNG, WebP, HEIC, or TIFF format',
    500: 'OCR service temporarily unavailable',
  };

  const message = errorMap[response.status] || 'An unexpected error occurred';

  return {
    success: response.status === 200,
    data: response.status === 200 ? data : undefined,
    message,
  };
};

// Parse OCR data to extract relevant information
const parseOcrData = (data: OcrFieldResponse[], fields: string[]): OcrFieldResult => {
  const result: OcrFieldResult = {};

  // Parse the response based on the actual API structure
  fields.forEach((field) => {
    const question = getOcrQuestion(field);
    const answer = data.find((item) => item.question === question)?.answer;

    // TODO: consider confidence thresholding in the future
    if (answer) {
      result[field] = answer;
    }
  });

  return result;
};

const getOcrQuestion = (field: string): string => {
  return `What is ${field}?`;
};

const useOcrProcessing = () => {
  const [loading, setLoading] = useState(false);

  const performOcrSearch = useCallback(
    async (base64Image: string, fields: string[]): Promise<OcrResult> => {
      setLoading(true);

      const requestBody = {
        queries: fields.map((field) => getOcrQuestion(field)),
        document: base64Image,
        autoselect: false,
      };

      try {
        if (!OCR_BASE_URL) {
          return { result: null, error: 'OCR URL is not defined' } as OcrResult;
        }

        const response = await fetch(OCR_BASE_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(requestBody),
        });

        const data = await response.json();
        const ocrResponse = handleOCRResponse(response, data);

        if (ocrResponse.success && ocrResponse.data) {
          const parsedData = parseOcrData(ocrResponse.data, fields);
          return { result: parsedData, error: null } as OcrResult;
        } else {
          return {
            result: null,
            error: ocrResponse.message || 'OCR processing failed',
          } as OcrResult;
        }
      } catch (err: any) {
        const errorMessage = err instanceof Error ? err.message : 'Network error occurred';
        return { result: null, error: errorMessage } as OcrResult;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const processImageFile = useCallback(
    async (file: File, fields: string[]): Promise<OcrResult> => {
      try {
        validateFile(file);
        const base64Image = await fileToBase64(file);
        return await performOcrSearch(base64Image, fields);
      } catch (err: any) {
        return { result: null, error: err.message || 'File processing failed' } as OcrResult;
      }
    },
    [performOcrSearch],
  );

  const getOcrSuccessMessage = (populatedFields: string[]): string | null => {
    if (populatedFields.length === 0) return null;
    const verb = populatedFields.length === 1 ? 'was' : 'were';
    const fieldsText = populatedFields.join(', ');
    return `${fieldsText} ${verb} populated automatically. Please fill the rest of the mandatory fields.`;
  };

  return {
    processImageFile,
    performOcrSearch,
    loading,
    getOcrSuccessMessage,
  };
};

export default useOcrProcessing;
